import React, { useState } from 'react'
import { ComponentConfig } from '@puckeditor/core'
import type { ThemeCustomFields } from '../../theme-config'
import { withStyleFields } from '../StyleWrapper'
import { withLayout } from '../Layout'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'
import * as styles from './Accordion.module.scss'

type AccordionItem = {
  title: string
  content: string
}

export type AccordionProps = {
  items: AccordionItem[]
  borderColor: string
  mode: 'single' | 'multiple'
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

const AccordionRender: React.FC<AccordionProps> = ({ items, borderColor, mode, background, border }) => {
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set())

  if (!items || items.length === 0) {
    return <div className={styles.placeholder}>No accordion items</div>
  }

  const toggle = (index: number) => {
    setOpenIndexes(prev => {
      if (mode === 'single') {
        return prev.has(index) ? new Set() : new Set([index])
      }
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const containerStyle = {
    ...(borderColor ? { '--border-color': borderColor } as React.CSSProperties : undefined),
    ...applyStyleProps({ background, border }),
  }

  return (
    <div className={styles.wrapper} style={containerStyle}>
      <div className={styles.accordion}>
        {items.map((item, index) => {
          const isOpen = openIndexes.has(index)
          return (
            <div key={index} className={styles.tab}>
              <div
                className={styles.tabLabel}
                onClick={() => toggle(index)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggle(index)
                  }
                }}
              >
                <span>{item.title}</span>
                <span
                  className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}
                />
              </div>
              <div
                className={`${styles.tabContent} ${isOpen ? styles.expanded : ''}`}
              >
                <div className={styles.contentInner}>
                  <div className={styles.contentText}>{item.content}</div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// withLayout adds padding/margin/size on a wrapper (BR-5406); container
// background/border stay on the inner withStyleFields, and `borderColor`
// remains bespoke (it styles the per-item dividers, not the container).
export const PuckAccordion = withLayout(
  withStyleFields<ComponentConfig<{
    props: AccordionProps
    fields: ThemeCustomFields
  }>>({
  label: 'Accordion',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Collapsible rows of a title and plain-text content (FAQs, disclaimers).',
    fields: {
      mode: 'single keeps one row open at a time.',
    },
  },
  defaultProps: {
    items: [
      {
        title: 'Disclaimer',
        content: 'Add your disclaimer text here.',
      },
    ],
    borderColor: '#e5e7eb',
    mode: 'single',
  },
  fields: {
    mode: {
      type: 'radio',
      label: 'Open Behavior',
      options: [
        { label: 'One at a time', value: 'single' },
        { label: 'Multiple', value: 'multiple' },
      ],
    },
    items: {
      type: 'array',
      label: 'Items',
      arrayFields: {
        title: { type: 'text', label: 'Title' },
        content: { type: 'textarea', label: 'Content' },
      },
      getItemSummary: (item: AccordionItem, index?: number) =>
        item?.title || `Item #${(index ?? 0) + 1}`,
    },
    borderColor: { type: 'colorPicker', label: 'Item Border Color' },
  },
    render: props => <AccordionRender {...props} />,
  }),
  { disableStyleFields: true },
)
